import React, { FC, PropsWithChildren, ReactNode } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import classNames from 'classnames';
import UnlimitedNavigation from '../navigation/UnlimitedNavigation';
import SearchNavigation from '../search/SearchNavigation';
import { OrdersNavigationBreadcrumbs } from '../../shop/orders/OrdersNavigation';
import { BundleDetailNavigation } from '../navigation/BundleDetailNavigation';
import CartNavigation from '../navigation/CartNavigation';
import { CartPreview } from '../../shop/cart/CartPreview';
import CatalogueNavigation from '../../shop/catalogue/CatalogueNavigation';
import OrderAssistantNavigation from '../navigation/OrderAssistantNavigation';
import YourBorrowBoxNavigation from '../../yourborrowbox/navigation/YourBorrowBoxNavigation';
import CollectionNavigation from '../../yourborrowbox/navigation/CollectionNavigation';
import LocalContentNavigation from '../../yourborrowbox/navigation/LocalContentNavigation';
import LibxNavigation from '../../yourborrowbox/navigation/LibxNavigation';
import ReserveCheckoutNavigation from '../../shop/reserve/components/ReserveCheckoutNavigation';
import ForeignLanguageNavigation from '../navigation/ForeignLanguageNavigation';
import LoteNavigation from '../navigation/LoteNavigation';
import LanguageLearningNavigation from '../navigation/LanguageLearningNavigation';
import MarketingNavigation from '../../yourborrowbox/navigation/MarketingNavigation';
import { SpocSelector } from './SpocSelector';
import MyAccountDropDown from './MyAccountDropDown';
import { SelectionListInfo } from './SelectionListInfo';
import MainNavigation from './MainNavigation';
import borrowBoxLogo from '../../../img/BorrowBox_logo.png';
import { LibXProductDetailNavigation, ShopProductDetailNavigation } from '../navigation/ProductDetailNavigation';
import { UseCartSummaryQuery } from '../../api/shopping-cart/useCartSummaryQuery';
import ShopNavigation from '../../shop/ShopNavigation';
import CollectionManagementNavigation from '../../yourborrowbox/navigation/CollectionManagementNavigation';
import MerchandisingNavigation from '../../yourborrowbox/navigation/MerchandisingNavigation';

export type PageHeaderProps = {
    readonly cartSummaryQuery: UseCartSummaryQuery;
};

export const PageHeader: FC<PropsWithChildren<PageHeaderProps>> = ({ children, cartSummaryQuery }) => {
    return (
        <>
            <div className="header-top header-container">
                <MyAccountDropDown />
                <SpocSelector />
            </div>
            <div className="header-main header-container">
                {/* =================================== First-Level-Navigation =================================== */}
                <div className="first-line">
                    <div className="logo">
                        <Link to="/">
                            <img src={borrowBoxLogo} alt="BorrowBox" />
                        </Link>
                    </div>
                    {children}
                    <MainNavigation />
                    <div className="main-nav-right fl-right display-flex">
                        <SelectionListInfo />
                        <CartPreview cartSummaryQuery={cartSummaryQuery} />
                    </div>
                </div>
                {/* =================================== Second-Level-Navigation =================================== */}
                <Routes>
                    <Route
                        path={'/assistant/reserve/:reserveListId/checkout'}
                        element={
                            <SecondLine>
                                <ReserveCheckoutNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path={'/collectionmgmt/reserves/:reserveListId/checkout'}
                        element={
                            <SecondLine>
                                <ReserveCheckoutNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/assistant/reserves/*"
                        element={
                            <SecondLine>
                                <OrderAssistantNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/assistant/subscriptions/*"
                        element={
                            <SecondLine>
                                <OrderAssistantNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/bundles/:bundleId"
                        element={
                            <SecondLine>
                                <BundleDetailNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/shop/*"
                        element={
                            <SecondLine>
                                <ShopNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/catalogue/*"
                        element={
                            <SecondLine>
                                <CatalogueNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/checkout/*"
                        element={
                            <SecondLine>
                                <CartNavigation isSticky={false} isCheckout={true} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path={'/collection/search/*'}
                        element={
                            <SecondLine>
                                <SearchNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path={'/collectionmgmt/search/*'}
                        element={
                            <SecondLine>
                                <SearchNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route path="/localcontent/products/:id" />
                    <Route
                        path={'/collection/*'}
                        element={
                            <YourBorrowBoxSecondLine>
                                <YourBorrowBoxNavigation />
                            </YourBorrowBoxSecondLine>
                        }
                    />
                    <Route
                        path={'/localcontent/*'}
                        element={
                            <YourBorrowBoxSecondLine>
                                <YourBorrowBoxNavigation />
                            </YourBorrowBoxSecondLine>
                        }
                    />
                    <Route
                        path={'/marketing/*'}
                        element={
                            <YourBorrowBoxSecondLine>
                                <YourBorrowBoxNavigation />
                            </YourBorrowBoxSecondLine>
                        }
                    />
                    <Route
                        path={'/collectionmgmt/*'}
                        element={
                            <YourBorrowBoxSecondLine>
                                <YourBorrowBoxNavigation />
                            </YourBorrowBoxSecondLine>
                        }
                    />
                    <Route
                        path={'/merchandising/*'}
                        element={
                            <YourBorrowBoxSecondLine>
                                <YourBorrowBoxNavigation />
                            </YourBorrowBoxSecondLine>
                        }
                    />
                    <Route
                        path={'/usermanagement/*'}
                        element={
                            <YourBorrowBoxSecondLine>
                                <YourBorrowBoxNavigation />
                            </YourBorrowBoxSecondLine>
                        }
                    />
                    <Route
                        path={'/reporting/*'}
                        element={
                            <YourBorrowBoxSecondLine>
                                <YourBorrowBoxNavigation />
                            </YourBorrowBoxSecondLine>
                        }
                    />
                    <Route
                        path="/language/lote/:languageId/featured"
                        element={
                            <SecondLine>
                                <LoteNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/language/lote/:languageId/newlyadded"
                        element={
                            <SecondLine>
                                <LoteNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/language/lote/:languageId/genres/*"
                        element={
                            <SecondLine>
                                <LoteNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/language/lote/:languageId/products/*"
                        element={
                            <SecondLine>
                                <LoteNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/language/languagelearning/:languageId/featured/*"
                        element={
                            <SecondLine>
                                <LanguageLearningNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/language/languagelearning/:languageId/newlyadded/*"
                        element={
                            <SecondLine>
                                <LanguageLearningNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/language/languagelearning/:languageId/products/*"
                        element={
                            <SecondLine>
                                <LanguageLearningNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/language/*"
                        element={
                            <SecondLine>
                                <ForeignLanguageNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/libxchange/products/:productId"
                        element={
                            <SecondLine>
                                <LibXProductDetailNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/libxchange/*"
                        element={
                            <SecondLine>
                                <LibxNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/orders/:orderId?"
                        element={
                            <SecondLine>
                                <OrdersNavigationBreadcrumbs isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/products/:productId"
                        element={
                            <SecondLine>
                                <ShopProductDetailNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/search/*"
                        element={
                            <SecondLine>
                                <SearchNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                    <Route
                        path="/unlimited/*"
                        element={
                            <SecondLine>
                                <UnlimitedNavigation isSticky={false} />
                            </SecondLine>
                        }
                    />
                </Routes>
                {/* horizontal rule between second and third-line */}
                {/* =================================== Third-Level-Navigation =================================== */}
                <Routes>
                    <Route
                        path={'/collectionmgmt/collection/*'}
                        element={
                            <ThirdLine>
                                <CollectionManagementNavigation
                                    isSticky={false}
                                    showLoanFormatSelector={true}
                                    showAgeCategorySelector={true}
                                />
                            </ThirdLine>
                        }
                    />
                    <Route
                        path={'/collectionmgmt/campaigns/*'}
                        element={
                            <ThirdLine>
                                <CollectionManagementNavigation
                                    isSticky={false}
                                    showLoanFormatSelector={true}
                                    showAgeCategorySelector={true}
                                />
                            </ThirdLine>
                        }
                    />
                    <Route path="/collectionmgmt/reserves/:reserveListId/checkout" />
                    <Route
                        path={'/collectionmgmt/reserves/*'}
                        element={
                            <ThirdLine>
                                <CollectionManagementNavigation
                                    isSticky={false}
                                    showLoanFormatSelector={false}
                                    showAgeCategorySelector={false}
                                />
                            </ThirdLine>
                        }
                    />
                    <Route
                        path={'/collectionmgmt/expiries/*'}
                        element={
                            <ThirdLine>
                                <CollectionManagementNavigation
                                    isSticky={false}
                                    showLoanFormatSelector={false}
                                    showAgeCategorySelector={false}
                                />
                            </ThirdLine>
                        }
                    />
                    <Route
                        path={'/collectionmgmt/subscriptions/*'}
                        element={
                            <ThirdLine>
                                <CollectionManagementNavigation
                                    isSticky={false}
                                    showLoanFormatSelector={false}
                                    showAgeCategorySelector={false}
                                />
                            </ThirdLine>
                        }
                    />
                    <Route
                        path="/merchandising/*"
                        element={
                            <ThirdLine>
                                <MerchandisingNavigation isSticky={false} />
                            </ThirdLine>
                        }
                    />
                    <Route path="/localcontent/products/:id" />
                    <Route
                        path="/localcontent/*"
                        element={
                            <ThirdLine>
                                <LocalContentNavigation isSticky={false} />
                            </ThirdLine>
                        }
                    />
                    <Route path="/collection/search/*" />
                    <Route
                        path="/collection/*"
                        element={
                            <ThirdLine>
                                <CollectionNavigation isSticky={false} />
                            </ThirdLine>
                        }
                    />
                    <Route
                        path="/marketing/*"
                        element={
                            <ThirdLine>
                                <MarketingNavigation isSticky={false} />
                            </ThirdLine>
                        }
                    />
                </Routes>
            </div>
        </>
    );
};

const SecondLine: FC<{ readonly className?: string; children: ReactNode }> = ({ className, children }) => {
    return <div className={classNames('second-line', className)}>{children}</div>;
};

const YourBorrowBoxSecondLine = ({ children }: { children: ReactNode }) => {
    return <SecondLine className="in-your-borrowbox">{children}</SecondLine>;
};

const ThirdLine: FC<{ readonly className?: string; children: ReactNode }> = ({ className, children }) => {
    return <div className={classNames('third-line', className)}>{children}</div>;
};
